import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/adminApi";
import LeadsTab from "./LeadsTab";
import JourneysTab from "./JourneysTab";
import TestimonialsTab from "./TestimonialsTab";
import FaqsTab from "./FaqsTab";

const TABS = [
  { id: "leads", label: "Leads" },
  { id: "journeys", label: "Journeys" },
  { id: "testimonials", label: "Testimonials" },
  { id: "faqs", label: "FAQs" },
];

export default function AdminPage() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || undefined);
  const [authError, setAuthError] = useState(location.state?.authError || null);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    if (location.state?.user) return;
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, [location.state]);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="admin-loading">
        <div className="text-[11px] uppercase tracking-[0.3em] text-foreground/60">Loading…</div>
      </div>
    );
  }

  if (!user) return <AdminLogin authError={authError} clearError={() => setAuthError(null)} />;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-foreground/15 bg-[#0A0B0A] text-[#F5F2EB]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-[#D1492E] rotate-45" />
            <span className="font-serif text-lg md:text-xl">
              Planet Air<span className="italic text-[#D1492E]"> Adventure</span>
            </span>
            <span className="ml-3 text-[10px] uppercase tracking-[0.28em] text-[#F5F2EB]/50 border border-[#F5F2EB]/25 px-2.5 py-1">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="text-[10px] uppercase tracking-[0.24em] text-[#F5F2EB]/70 hover:text-[#D1492E] transition-colors" data-testid="admin-view-site">
              View site
            </Link>
            <span className="hidden sm:block text-xs text-[#F5F2EB]/60" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={async () => {
                await api.post("/auth/logout").catch(() => {});
                setUser(null);
              }}
              data-testid="admin-logout"
              className="text-[10px] uppercase tracking-[0.24em] border border-[#F5F2EB]/40 px-4 py-2 hover:border-[#D1492E] hover:text-[#D1492E] transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <nav className="border-b border-foreground/15 bg-background sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-foreground/60 hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        {tab === "leads" && <LeadsTab />}
        {tab === "journeys" && <JourneysTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "faqs" && <FaqsTab />}
      </main>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function AdminLogin({ authError }) {
  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0B0A] text-[#F5F2EB] px-6" data-testid="admin-login">
      <div className="max-w-md w-full border border-[#F5F2EB]/15 p-10 md:p-14 text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="w-2.5 h-2.5 bg-[#D1492E] rotate-45" />
          <span className="font-serif text-2xl">
            Planet Air<span className="italic text-[#D1492E]"> Adventure</span>
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5F2EB]/55 mb-3">
          Admin Panel
        </div>
        <p className="text-sm text-[#F5F2EB]/65 font-light leading-relaxed mb-8">
          Sign in with the authorised Google account to manage leads, journeys,
          testimonials and FAQs.
        </p>
        {authError && (
          <div className="mb-6 border border-[#D1492E]/50 bg-[#D1492E]/10 px-4 py-3 text-sm text-[#D1492E]" data-testid="admin-auth-error">
            {authError}
          </div>
        )}
        <button
          onClick={signIn}
          data-testid="admin-google-signin"
          className="btn-sharp w-full inline-flex items-center justify-center gap-3 border border-[#F5F2EB] px-6 py-4 text-[11px] uppercase tracking-[0.22em] font-medium"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.96 10.96 0 001 12c0 1.78.43 3.45 1.18 4.94l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
          </svg>
          Sign in with Google
        </button>
        <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/40">
          Restricted access · Paro, Bhutan
        </p>
      </div>
    </div>
  );
}
